import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ArrowRight, Building2, ShieldCheck, Search, FileSpreadsheet, BookOpen, LifeBuoy,
} from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { SID } from "@/constants/siteIds";
import { trackEvent } from "@/lib/analytics";

const QUICK_LINKS = [
  { to: "/cosecapp", Icon: Building2, t: "CoSec App", d: "Entity & compliance platform" },
  { to: "/coverify", Icon: ShieldCheck, t: "CoVerify", d: "ID verification & KYC" },
  { to: "/cosearch", Icon: Search, t: "CoSearch", d: "UK company intelligence" },
  { to: "/coform", Icon: FileSpreadsheet, t: "CoForm", d: "Form a UK company" },
  { to: "/blog", Icon: BookOpen, t: "Blog", d: "Compliance & Cosec insights" },
  { to: "/contact", Icon: LifeBuoy, t: "Contact", d: "Talk to a human" },
];

export default function NotFoundPage() {
  useEffect(() => {
    // Fire a GA4 event so we can spot broken inbound links from search/ads
    trackEvent("page_not_found", {
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
    });
  }, []);

  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

        <div className="container-cv relative py-20 md:py-28 max-w-4xl mx-auto">
          {/* 404 */}
          <div className="text-center">
            <div className="font-display text-[7rem] md:text-[10rem] font-black text-primary leading-none tracking-tight">
              4<span className="text-accent">0</span>4
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight">
              That page wandered off.
            </h1>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
              The link you followed may be broken, the page may have moved, or it may never have existed.
              Let's get you back to something useful.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild data-testid={SID.notFoundHome} className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                <Link to="/"><ArrowLeft className="mr-2 h-4 w-4" /> Back to home</Link>
              </Button>
              <Button asChild variant="outline" className="h-12 px-6 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
                <Link to="/cosecapp">Explore the platform <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>

          {/* Quick links */}
          <div className="mt-16">
            <div className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              Popular destinations
            </div>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {QUICK_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`not-found-link-${l.to.slice(1)}`}
                  className="group flex items-center gap-3 rounded-xl border border-border bg-white p-4 hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-md transition-all"
                >
                  <div className="h-10 w-10 grid place-items-center rounded-lg bg-primary/10 text-primary group-hover:bg-accent group-hover:text-primary transition-colors shrink-0">
                    <l.Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-primary text-sm leading-tight">{l.t}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{l.d}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
